import { useEffect, useState } from 'react';
import { t } from '@lingui/macro';

import { useApiClient } from '../../core/libs/api/api-client.provider';
import { HttpResource } from '../../core/libs/http-resource';
import { AppNotify } from '../../features/app-notify/app-notify';
import { useLocal } from '../../lib/local/use-local';
import { useProfile } from '../profile/use-profile';
import {
  NewPushSubscription,
  pushSubscriptionRequest,
} from './push-subscribe.request';
import { pushUnsubscribeRequest } from './push-unsubscribe.request';

type PushNotifyAccessState =
  | { state: 'idle' }
  | { state: 'default' }
  | { state: 'denied' }
  | { state: 'granted'; subscription: PushSubscription | null }
  | { state: 'unsupported' }
  | { state: 'error'; message: string; cause: Error };

const requestPermission = (): Promise<NotificationPermission> => {
  return new Promise<NotificationPermission>(function (resolve, reject) {
    // Поддержка устаревшей версии с коллбеком
    const permissionResult = Notification.requestPermission(function (result) {
      resolve(result);
    });

    if (permissionResult) {
      permissionResult.then(resolve, reject);
    }
  });
};
// стоит получать состояние из navigator.permissions.query для push и notifications
const getPushNotifyAccessState = async (): Promise<PushNotifyAccessState> => {
  if (!('serviceWorker' in navigator)) return { state: 'unsupported' };
  if (!('PushManager' in window)) return { state: 'unsupported' };

  if (Notification.permission === 'denied') return { state: 'denied' };
  if (Notification.permission === 'default') return { state: 'default' };

  let pushSubscription: PushSubscription | null = null;

  try {
    const swRegistration = await navigator.serviceWorker.ready;
    pushSubscription = await swRegistration.pushManager.getSubscription();

    if (!pushSubscription) return { state: 'default' };
  } catch (o_O) {
    console.error(o_O);

    return {
      state: 'error',
      message: t`Не получилось создать подписку на уведомления. Попробуйте перезапустить приложение`,
      cause: o_O as Error,
    };
  }

  return { state: 'granted', subscription: pushSubscription };
};

const listenPushNotifyAccessState = async ({
  onChange,
  onError,
  abort,
}: {
  abort: AbortController;
  onChange: (state: PushNotifyAccessState) => void;
  onError: (error: Error) => void;
}): Promise<void> => {
  let previousState = null as unknown as PushNotifyAccessState['state'];

  const interval = window.setInterval(() => {
    getPushNotifyAccessState()
      .then((currentState) => {
        // console.log({ previousState, currentState });
        if (previousState !== currentState.state) {
          onChange(currentState);
        }

        previousState = currentState.state;
      })
      .catch(onError);
  }, 1000);

  abort.signal.addEventListener('abort', () => {
    clearInterval(interval);
  });
};

const subscribePushNotify = async ({
  publicKey,
}: {
  publicKey: string;
}): Promise<
  | {
      state: 'default' | 'denied' | 'unsupported' | 'error';
    }
  | { state: 'error'; message: string; cause: Error }
  | { state: 'granted'; subscription: PushSubscription }
> => {
  try {
    switch (Notification.permission) {
      case 'denied':
        console.info({ Notification: 'denied' });

        return { state: 'denied' };
      case 'granted':
        break;
      default: {
        const notifyPermState = await requestPermission();

        if (notifyPermState === 'denied') return { state: 'denied' };
        if (notifyPermState === 'default') return { state: 'default' };
      }
    }

    // We need the service worker registration to access the push manager
    try {
      const reg = await navigator.serviceWorker.ready;
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: publicKey,
      });

      if (!subscription) return { state: 'default' };

      return { state: 'granted', subscription };
    } catch (err) {
      console.error(err);

      return {
        state: 'error',
        cause: err as Error,
        message: t`Не получилось создать подписку на уведомления. Попробуйте перезапустить приложение`,
      };
    }
  } catch (err) {
    console.error(err);
    return {
      state: 'error',
      cause: err as Error,
      message: t`Ошибка при подписке на уведомления. Попробуйте позднее`,
    };
  }
};

export const usePushNotifyPerms = () => {
  useLocal();

  const userData = useProfile();
  const publicKey = userData.webpushPublicKey;
  const personId = userData.profile.id;

  const [permState, setPermState] = useState<PushNotifyAccessState>({
    state: 'idle',
  });

  const [notify] = useState(() => new AppNotify());
  const [browserApiWait, setBrowserApiWait] = useState(false);

  const apiClient = useApiClient();

  const [pushSubscribeRequest] = useState(
    () =>
      new HttpResource<NewPushSubscription, NewPushSubscription>({
        apiClient,
        httpRequest: pushSubscriptionRequest,
        errorNotify: notify,
        onFinish() {
          setBrowserApiWait(false);
          setPermState({ state: 'granted', subscription: null });
        },
        onError() {
          setBrowserApiWait(false);
          setPermState({ state: 'default' });
        },
      })
  );

  const [pushUnsubRequest] = useState(
    () =>
      new HttpResource<NewPushSubscription, NewPushSubscription>({
        apiClient,
        httpRequest: pushUnsubscribeRequest,
        errorNotify: notify,
        onFinish() {
          setBrowserApiWait(false);
          setPermState({ state: 'default' });
        },
        onError() {
          setBrowserApiWait(false);
          setPermState({ state: 'default' });
        },
      })
  );

  const isLoading =
    browserApiWait ||
    pushSubscribeRequest.isLoading ||
    pushUnsubRequest.isLoading;

  const isActive = permState.state === 'granted';

  const subscribe = async () => {
    setBrowserApiWait(true);

    const result = await subscribePushNotify({
      publicKey,
    });

    if (result.state === 'granted') {
      const subscription = result.subscription.toJSON();

      pushSubscribeRequest.send({
        personId,
        subscription: {
          endpoint: subscription.endpoint || 'no-endpoint',
          keys: {
            auth: subscription.keys?.auth || 'no-key',
            p256dh: subscription.keys?.p256dh || 'no-key',
          },
        },
      });

      return;
    }

    const notifyError = () => {
      notify.addMessage({
        type: 'error',
        title: t`Уведомления`,
        message: t`Не удалось выполнить подписку в приложении`,
      });
    };

    notifyError();
  };

  const unsubscribe = async () => {
    setBrowserApiWait(true);

    const pushNotifyState = await getPushNotifyAccessState();

    if (pushNotifyState.state !== 'granted') {
      setPermState(pushNotifyState);

      setBrowserApiWait(false);
      return;
    }

    const pushSubscription = pushNotifyState.subscription;

    const subscription = pushSubscription?.toJSON();

    pushSubscription?.unsubscribe();

    pushUnsubRequest.send({
      personId,
      subscription: {
        endpoint: subscription?.endpoint || 'no-endpoint',
        keys: {
          auth: subscription?.keys?.auth || 'no-key',
          p256dh: subscription?.keys?.p256dh || 'no-key',
        },
      },
    });
  };

  useEffect(() => {
    const abort = new AbortController();

    getPushNotifyAccessState()
      .then((state) => setPermState(state))
      .catch((error) => console.error(error));

    listenPushNotifyAccessState({
      abort,
      onChange: (state) => setPermState(state),
      onError: (error) => console.error(error),
    });

    return () => abort.abort();
  }, []);

  return [
    { permState, isLoading, isActive },
    { subscribe, unsubscribe },
  ] as const;
};

// 1. Добавить сообщения об ошибках +
// 2. Вынести все в отдельный useAppPushNotify(); +
// 3. Перепроверить стейты
// 4. Проверять, если подписка и пушить в апи????
